import React, { useState } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Chip,
  Grid,
  TextField,
  Stack,
  Autocomplete,
  Divider,
} from '@mui/material';
import {
  Key as KeyIcon,
} from '@mui/icons-material';
import {
  UnifiedTableData,
  TableSummary,
  ColumnSummary,
  JoinDefinition,
  ENTITY_TYPE_SUGGESTIONS,
} from '../../types/unifiedSchema';
import { isColumnInList } from '../../hooks/useUnifiedSchema';
import TableOverviewSection from './TableOverviewSection';
import UnifiedColumnTable from './UnifiedColumnTable';
import DataPreviewPanel from './DataPreviewPanel';
import RelationshipEditor from '../RelationshipEditor';
import RelationshipDiagram from '../RelationshipDiagram';
import OutputStructureTab from './OutputStructureTab';

interface TableDetailTabsProps {
  table: UnifiedTableData;
  tableIndex: number;
  batchId: string;
  summaries: TableSummary[];
  /** Controlled active tab index (0=Overview, 1=Columns, 2=Keys&Joins, 3=Preview, 4=Structure) */
  activeTab?: number;
  /** Called when user switches tab */
  onTabChange?: (tabIndex: number) => void;
  onUpdateTable: (index: number, field: keyof TableSummary, value: string) => void;
  onUpdateColumn: (tableIndex: number, colIndex: number, updates: Partial<ColumnSummary>) => void;
  onTogglePrimaryKey: (tableIndex: number, columnName: string, isPk: boolean) => void;
  onUpdateJoins: (tableIndex: number, updatedJoins: JoinDefinition[]) => void;
  onOpenColumnEditor: (tableIndex: number, colIndex: number, column: ColumnSummary) => void;
  /** Schema-level output structure. When present, shows a "Structure" tab. */
  outputStructure?: Record<string, any> | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  if (value !== index) return null;
  return (
    <Box sx={{ py: 2 }} role="tabpanel">
      {children}
    </Box>
  );
};

const TableDetailTabs: React.FC<TableDetailTabsProps> = ({
  table,
  tableIndex,
  batchId,
  summaries,
  activeTab: controlledTab,
  onTabChange,
  onUpdateTable,
  onUpdateColumn,
  onTogglePrimaryKey,
  onUpdateJoins,
  onOpenColumnEditor,
  outputStructure,
}) => {
  const [internalTab, setInternalTab] = useState(0);
  const activeTab = controlledTab ?? internalTab;
  const setActiveTab = (v: number) => {
    setInternalTab(v);
    onTabChange?.(v);
  };

  const currentSummary = summaries[tableIndex];
  if (!currentSummary) return null;

  const columnSummaries = currentSummary.column_summaries || [];

  return (
    <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 2 }}>
      {/* Table header */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" component="div">
          {table.file_name}
          {table.semantic_name && table.semantic_name !== table.file_name && (
            <Typography component="span" color="primary.main" sx={{ ml: 1 }}>
              → {table.semantic_name}
            </Typography>
          )}
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
          <Chip label={table.entity_type || 'unknown'} size="small" color="primary" variant="outlined" />
          <Chip label={`${table.columns.length} columns`} size="small" variant="outlined" />
          {table.row_count > 0 && (
            <Chip label={`${table.row_count.toLocaleString()} rows`} size="small" variant="outlined" />
          )}
          {table.file_size_mb > 0 && (
            <Chip label={`${table.file_size_mb.toFixed(1)} MB`} size="small" variant="outlined" />
          )}
        </Stack>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label="Overview" />
          <Tab label="Columns" />
          <Tab label="Keys & Joins" />
          <Tab label="Preview" />
          {outputStructure && <Tab label="Structure" />}
        </Tabs>
      </Box>

      {/* Tab 0: Overview */}
      <TabPanel value={activeTab} index={0}>
        {/* Editable table fields */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Semantic Name"
              value={currentSummary.semantic_name || ''}
              onChange={(e) => onUpdateTable(tableIndex, 'semantic_name', e.target.value)}
              helperText="Meaningful display name"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              freeSolo
              options={ENTITY_TYPE_SUGGESTIONS}
              value={currentSummary.entity_type || ''}
              onChange={(_, newValue) =>
                onUpdateTable(tableIndex, 'entity_type', (newValue || '').toLowerCase().replace(/\s+/g, '_'))
              }
              onInputChange={(_, newInputValue) =>
                onUpdateTable(tableIndex, 'entity_type', newInputValue.toLowerCase().replace(/\s+/g, '_'))
              }
              renderInput={(params) => (
                <TextField {...params} size="small" label="Entity Type" helperText="Type or select" />
              )}
              getOptionLabel={(option) =>
                option.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
              }
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              label="Description"
              multiline
              minRows={1}
              maxRows={3}
              value={currentSummary.description || ''}
              onChange={(e) => onUpdateTable(tableIndex, 'description', e.target.value)}
              helperText="Brief description of this table"
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Stats cards */}
        <Typography variant="subtitle2" gutterBottom>
          Statistical Overview
        </Typography>
        <TableOverviewSection table={table} />
      </TabPanel>

      {/* Tab 1: Columns */}
      <TabPanel value={activeTab} index={1}>
        <UnifiedColumnTable
          columns={table.columns}
          primaryKeyCandidates={table.primary_key_candidates}
          foreignKeyCandidates={table.foreign_key_candidates}
          tableIndex={tableIndex}
          onUpdateColumn={onUpdateColumn}
          onOpenColumnEditor={onOpenColumnEditor}
        />
      </TabPanel>

      {/* Tab 2: Keys & Joins */}
      <TabPanel value={activeTab} index={2}>
        {/* Primary Keys */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="subtitle2"
            sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <KeyIcon fontSize="small" color="success" /> Primary Keys
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            {(table.primary_key_candidates || []).map((pk) => (
              <Chip
                key={pk}
                icon={<KeyIcon />}
                label={pk}
                size="small"
                color="success"
                variant="outlined"
                onDelete={() => onTogglePrimaryKey(tableIndex, pk, false)}
              />
            ))}
            <Autocomplete
              size="small"
              options={columnSummaries
                .filter((c) => !isColumnInList(c.name, table.primary_key_candidates))
                .map((c) => c.name)}
              value={null}
              onChange={(_, newValue) => {
                if (newValue) onTogglePrimaryKey(tableIndex, newValue, true);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Add PK column..."
                  variant="outlined"
                  size="small"
                  sx={{ minWidth: 160 }}
                />
              )}
              sx={{ minWidth: 160 }}
              blurOnSelect
              clearOnBlur
            />
          </Stack>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Relationships */}
        <RelationshipEditor
          tableIndex={tableIndex}
          tableName={currentSummary.file_name}
          joins={currentSummary.likely_joins || []}
          allTables={summaries}
          currentTableColumns={columnSummaries}
          onChange={onUpdateJoins}
        />

        {/* Focused ER Diagram — current table + related tables with all columns */}
        {summaries.length >= 2 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Relationship Diagram
            </Typography>
            <RelationshipDiagram
              tables={summaries}
              focusedTableId={currentSummary.file_name}
              height={360}
            />
          </Box>
        )}
      </TabPanel>

      {/* Tab 3: Preview */}
      <TabPanel value={activeTab} index={3}>
        <DataPreviewPanel
          batchId={batchId}
          fileName={table.file_name}
          fileOrigin={table.file_origin}
          expanded={activeTab === 3}
        />
      </TabPanel>

      {outputStructure && (
        <TabPanel value={activeTab} index={4}>
          <OutputStructureTab outputStructure={outputStructure as any} />
        </TabPanel>
      )}
    </Box>
  );
};

export default TableDetailTabs;
